import { FC, useMemo } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/helper/utils";
import { PaginationProps, PaginationConfig } from "@/lib/types";
import { PaginationUtils } from "@/lib/helper";
import { usePagination } from "@/lib/hooks";

const defaultConfig: Required<PaginationConfig> = {
  maxVisiblePages: 7,
  showEllipsis: true,
  showFirstLast: true,
};

const PaginationApi: FC<PaginationProps> = ({
  pageIndex,
  setPageIndex,
  data,
  isLoading = false,
  className,
  config = {},
}) => {
  const {
    currentPage,
    totalPages,
    handlePageChange: onPageChange,
  } = usePagination({
    data,
    initialPage: pageIndex,
    onPageChange: setPageIndex,
  });

  const mergedConfig = useMemo(
    () => ({
      ...defaultConfig,
      ...config,
    }),
    [config]
  );

  const pageNumbers = useMemo(
    () =>
      PaginationUtils.generatePageNumbers(
        currentPage,
        totalPages,
        mergedConfig.maxVisiblePages
      ),
    [currentPage, totalPages, mergedConfig.maxVisiblePages]
  );

  const handlePageClick = (page: number) => {
    if (isLoading || page === currentPage) return;
    onPageChange(page);
  };

  const handlePrevious = () => {
    if (isLoading || currentPage <= 1) return;
    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (isLoading || currentPage >= totalPages) return;
    onPageChange(currentPage + 1);
  };

  if (totalPages <= 1 || !totalPages) return null;

  return (
    <Pagination className={cn("mx-auto flex w-full justify-center", className)}>
      <PaginationContent>
        {/* Previous Button */}
        <PaginationItem className="h-[30px] w-[30px]">
          <PaginationPrevious
            onClick={handlePrevious}
            className={cn(
              "cursor-pointer",
              (currentPage <= 1 || isLoading) &&
                "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage <= 1 || isLoading}
          />
        </PaginationItem>

        {/* Page Numbers */}
        {pageNumbers.map((pageNum, index) => (
          <PaginationItem key={`${pageNum}-${index}`}>
            {pageNum === "ellipsis" ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                onClick={() => handlePageClick(pageNum)}
                isActive={pageNum === currentPage}
                className={cn(
                  "cursor-pointer rounded-full",
                  isLoading && "pointer-events-none opacity-50"
                )}
                aria-current={pageNum === currentPage ? "page" : undefined}
              >
                {pageNum}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        {/* Next Button */}
        <PaginationItem className="h-[30px] w-[30px]">
          <PaginationNext
            onClick={handleNext}
            className={cn(
              "cursor-pointer",
              (currentPage >= totalPages || isLoading) &&
                "pointer-events-none opacity-50"
            )}
            aria-disabled={currentPage >= totalPages || isLoading}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export default PaginationApi;
